import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

// ─── Tipos ─────────────────────────────────────────────────────────

export interface BancoPlazaHealth {
  status: 'ok' | 'degraded';
  stage: string;
  configLoaded: boolean;
  circuitBreaker: {
    state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
    consecutiveFailures: number;
    circuitOpenedAt: string | null;
  };
  pagoBancoPlazaEnabled: boolean;
  timestamp: string;
}

export interface BancoPlazaCuenta {
  numero: string;
  fechaApertura: string;
  tipoCuenta: string;
  estatus: 'A' | 'T' | 'O';
  moneda: string;
  saldoDisponible: number;
}

export interface BancoPlazaCuentasListResponse {
  conteoCuentas: number;
  cuentas: BancoPlazaCuenta[];
}

export interface BancoPlazaBalance {
  id: number;
  saldoCuenta: number;
  saldoDisponible: number;
  saldoRetenido: number;
  saldoTotal: number;
  ultimaTransaccionId: string | null;
  ultimaTransaccionTipo: string | null;
  updatedAt: string;
}

export interface BancoPlazaStats {
  desde: string;
  hasta: string;
  payins: { total: number; monto: number };
  payouts: {
    total: number;
    montoEnviado: number;
    pending: number;
    pendingMonto: number;
    aprobados: number;
    aprobadosMonto: number;
    rechazados: number;
  };
  /**
   * Cargos automáticos del banco del día (comisiones / impuestos / otros).
   * Opcional: backends pre-F9.2 no lo devuelven; el front debe degradar
   * graciosamente.
   */
  cargos?: {
    total: number;
    monto: number;
    comisiones: { total: number; monto: number };
    impuestos: { total: number; monto: number };
    otros: { total: number; monto: number };
  };
  balance: {
    saldoCuenta: number;
    saldoDisponible: number;
    saldoRetenido: number;
    saldoTotal: number;
  } | null;
}

/**
 * Tipos de transacción en `banco_plaza_transactions`.
 *
 * `PAYOUT` queda como legacy (registros previos a la separación por método).
 * Los flujos nuevos clasifican el payout según el camino:
 *  - PAYOUT_OWNBANK   → CCE intrabancaria (cod_banco_a === '0138')
 *  - PAYOUT_OTHERBANK → CCE interbancaria
 *  - PAYOUT_P2P       → Pago Móvil
 */
export type BancoPlazaTxTipo =
  | 'PAYIN'
  | 'PAYOUT'
  | 'PAYOUT_OWNBANK'
  | 'PAYOUT_OTHERBANK'
  | 'PAYOUT_P2P'
  /**
   * Cargo automático del banco: comisiones, impuestos (IGTF/RET/IMP),
   * mantenimiento, débitos automáticos. NO es payout iniciado por nosotros.
   * Detectado por payinSync con `mov.tipo === 'CO'` o prefijo de concepto.
   */
  | 'CARGO_BANCARIO';

/**
 * Status del log de transacción.
 *
 * `PENDING_VERIFICATION` ← nuevo: el banco PUDO haber procesado tras error
 * indeterminado (NETWORK_ERROR / RATE_LIMITED). El cron de verificación
 * cruza con movimientos del banco para aprobar o rechazar.
 */
export type BancoPlazaTxStatus =
  | 'PENDING'
  | 'PENDING_BCV'
  | 'PENDING_VERIFICATION'
  | 'APPROVED'
  | 'REJECTED'
  | 'EXPIRED';

/**
 * Categoría del error clasificada por el payout service.
 * Se usa para decidir entre rechazo categórico y verificación diferida.
 */
export type BancoPlazaErrorCategory =
  | 'NETWORK_ERROR'
  | 'RATE_LIMITED'
  | 'AUTH_INVALID'
  | 'VALIDATION_ERROR'
  | 'UNKNOWN';

export interface BancoPlazaTransaction {
  id: string;
  tipo: BancoPlazaTxTipo;
  referenciaInterna: string;
  referenciaBanco: string | null;
  status: BancoPlazaTxStatus;
  monto: number;
  moneda: string;
  identificacionOrdenante: string | null;
  identificacionBeneficiario: string | null;
  cuentaOrigen: string | null;
  cuentaDestino: string | null;
  telefono: string | null;
  codBancoDestino: string | null;
  nombreBeneficiario: string | null;
  concepto: string | null;
  tipoProposito: string | null;
  tipoInstrumentoB: string | null;
  codigoRespuesta: string | null;
  descripcionRespuesta: string | null;
  errorCategory: BancoPlazaErrorCategory | null;
  verificationAttempts: number;
  rawRequest: any;
  rawResponse: any;
  settlementRaw: any;
  walletCardexVesId: string | null;
  userId: string | null;
  transaccionId: number | null;
  operadorEmail: string | null;
  fechaMovimiento: string | null;
  horaMovimiento: string | null;
  naturaleza: string | null;
  /** `tipo` del movimiento devuelto por el banco (TB/Y9/DP/CO/...) */
  tipoMovimientoBanco?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  /**
   * Campos derivados del JOIN con `referencias_ves` cuando un payin BP fue
   * conciliado con una referencia interna que un operador "quemó" desde
   * admin. Espejo de los campos en N58MovementWithCredit. Solo poblados
   * en payins con `referenciaVesId NOT NULL` y rail BP.
   */
  isInternalCredit?: boolean;
  referenciaTitular?: string | null;
  referenciaTipoCuenta?: string | null;
  referenciaOperatorEmail?: string | null;

  /**
   * Nuestro lado del pago, que el backend cruza al leer el detalle: quién es
   * el cliente, qué beneficiario de su agenda y qué transacción interna.
   *
   * Los arma `ContraparteWalletService`, el mismo que alimenta el detalle de
   * N58 — por eso los campos se llaman igual en los dos dashboards. Sólo
   * vienen en `getTransaction`, nunca en el listado, y cualquiera puede ser
   * null: un cargo del banco no tiene cliente ni beneficiario.
   */
  cliente?: {
    usuarioId: number;
    nombre: string | null;
    apellidos: string | null;
    email: string | null;
    documento: string | null;
    paisResidencia: string | null;
  } | null;
  beneficiario?: {
    nombre: string | null;
    documento: string | null;
    cuenta: string | null;
    bancoNombre: string | null;
    bancoCodigo: string | null;
    esPagoMovil: boolean;
  } | null;
  transaccion?: {
    id: number;
    estado: string | null;
    referencia: string | null;
    montoOrigen: number | null;
    monedaOrigen: string | null;
    montoDestino: number | null;
    monedaDestino: string | null;
    operador: string | null;
    createdAt: string | null;
  } | null;
  referenciaVes?: {
    id: string;
    titular: string | null;
    banco: string | null;
    cuenta: string | null;
    tipoCuenta: string | null;
    monto: number | null;
    status: string | null;
    operatorEmail: string | null;
  } | null;
}

export interface BancoPlazaBalanceHistorial {
  id: string;
  saldoDisponible: number;
  saldoRetenido: number;
  saldoTotal: number;
  transaccionId: string;
  transaccionTipo: 'PAYIN' | 'PAYOUT' | 'CARGO_BANCARIO';
  movimiento: string;
  montoMovimiento: number;
  operador: string | null;
  createdAt: string;
}

export interface BancoPlazaTogglesStatus {
  pagoBancoPlaza: boolean;
  pagoN58: boolean;
  montoMin: number;
  cuentaTrabajo: number | null;
}

export interface BancoPlazaConflictError {
  status: 409;
  message: string;
  conflictWith: 'PAGO_N58' | 'PAGO_BANCO_PLAZA';
}

export interface BancoPlazaGenericError {
  status: number;
  message: string;
  conflictWith?: undefined;
}

export type BancoPlazaToggleError =
  | BancoPlazaConflictError
  | BancoPlazaGenericError;

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ListTransactionsParams {
  page?: number;
  limit?: number;
  tipo?: BancoPlazaTxTipo;
  status?: BancoPlazaTxStatus;
  referencia?: string;
  codBancoDestino?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  userId?: string;
}

// ─── Service ───────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class BancoPlazaService {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio Banco Plaza';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = error.error?.message || `Error del servidor: ${error.status}`;
    }
    return throwError(() => errorMessage);
  }

  // ─── Health ──────────────────────────────────────────────────────

  getHealth(): Observable<BancoPlazaHealth> {
    const url = `${this.baseUrl}banco-plaza/health`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<BancoPlazaHealth>(url, { headers })),
      catchError(this.handleError),
    );
  }

  // ─── Cuentas (saldo real banco) ──────────────────────────────────

  getCuentasDefault(): Observable<{ success: boolean; data: BancoPlazaCuentasListResponse }> {
    const url = `${this.baseUrl}banco-plaza/cuentas/default`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: BancoPlazaCuentasListResponse }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getCuentaDetalle(cuenta: string): Observable<{ success: boolean; data: BancoPlazaCuenta }> {
    const url = `${this.baseUrl}banco-plaza/cuentas/detalle/${cuenta}`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: BancoPlazaCuenta }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getCuentaPorTelefono(telefono: string): Observable<{ success: boolean; data: BancoPlazaCuenta }> {
    const url = `${this.baseUrl}banco-plaza/cuentas/telefono/${telefono}`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: BancoPlazaCuenta }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getMovimientosCuenta(
    cuenta: string,
    params: { anio: number; mes: number; dia?: number },
  ): Observable<{ success: boolean; data: any }> {
    const url = `${this.baseUrl}banco-plaza/cuentas/movimientos/${cuenta}`;
    const queryParams: Record<string, string> = {
      anio: String(params.anio),
      mes: String(params.mes),
    };
    if (params.dia !== undefined) queryParams['dia'] = String(params.dia);
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: any }>(url, { headers, params: queryParams })),
      catchError(this.handleError),
    );
  }

  // ─── Operations (dashboard) ──────────────────────────────────────

  getStats(): Observable<{ success: boolean; data: BancoPlazaStats }> {
    const url = `${this.baseUrl}banco-plaza/stats`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: BancoPlazaStats }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getBalance(): Observable<{ success: boolean; data: BancoPlazaBalance | null }> {
    const url = `${this.baseUrl}banco-plaza/balance`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: BancoPlazaBalance | null }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getBalanceHistorial(page = 1, limit = 20): Observable<PaginatedResponse<BancoPlazaBalanceHistorial>> {
    const url = `${this.baseUrl}banco-plaza/balance/historial`;
    const params = { page: String(page), limit: String(limit) };
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<PaginatedResponse<BancoPlazaBalanceHistorial>>(url, { headers, params })),
      catchError(this.handleError),
    );
  }

  getTransactions(params: ListTransactionsParams = {}): Observable<PaginatedResponse<BancoPlazaTransaction>> {
    const url = `${this.baseUrl}banco-plaza/transactions`;
    const httpParams: Record<string, string> = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') httpParams[k] = String(v);
    });
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<PaginatedResponse<BancoPlazaTransaction>>(url, { headers, params: httpParams })),
      catchError(this.handleError),
    );
  }

  getTransaction(id: string): Observable<{ success: boolean; data: BancoPlazaTransaction }> {
    const url = `${this.baseUrl}banco-plaza/transactions/${id}`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: BancoPlazaTransaction }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  /**
   * Descarga las transacciones que matchean los filtros como blob XLSX.
   * El backend aplica `AguacateOperatorGuard` (solo operadores).
   * Si el conjunto excede 50000 filas → 413 PayloadTooLarge.
   */
  exportTransactions(params: ListTransactionsParams = {}): Observable<Blob> {
    const url = `${this.baseUrl}banco-plaza/transactions/export`;
    const httpParams: Record<string, string> = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') httpParams[k] = String(v);
    });
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get(url, {
          headers,
          params: httpParams,
          responseType: 'blob',
        }),
      ),
      catchError(this.handleError),
    );
  }

  // ─── Triggers manuales de crons ──────────────────────────────────

  forzarPayinSync(): Observable<{ success: boolean; data: any }> {
    const url = `${this.baseUrl}banco-plaza/cron/payin-sync`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<{ success: boolean; data: any }>(url, {}, { headers })),
      catchError(this.handleError),
    );
  }

  forzarSettlement(): Observable<{ success: boolean; data: any }> {
    const url = `${this.baseUrl}banco-plaza/cron/settlement`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<{ success: boolean; data: any }>(url, {}, { headers })),
      catchError(this.handleError),
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // @temporary F12.1 BACKFILL — eliminar tras ejecución inicial en prod
  // ════════════════════════════════════════════════════════════════════
  /** @temporary F12.1 — eliminar tras ejecución inicial en prod */
  forzarSyncHistorico(): Observable<{
    success: boolean;
    data: {
      fechaApertura: string;
      desde: string;
      hasta: string;
      mesesProcesados: number;
      mesesFallidos: number;
      payinsTotal: number;
      payoutsTotal: number;
      conceptosSinAtribucionTotal: number;
      errores: Array<{ anio: number; mes: number; error: string }>;
    };
  }> {
    const url = `${this.baseUrl}banco-plaza/cron/payin-sync-historico`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<any>(url, {}, { headers }),
      ),
      catchError(this.handleError),
    );
  }
  // ════════════════════════════════════════════════════════════════════
  // Fin bloque @temporary F12.1
  // ════════════════════════════════════════════════════════════════════

  // ─── Toggles operativos (PAGO_BANCO_PLAZA / monto-min / cuenta-trabajo) ─

  /**
   * Lee los 3 toggles BP + el toggle PAGO_N58 en una sola llamada.
   * Útil para hidratar la UI sin doble round-trip.
   *
   * NOTA: usa `X-Skip-Error-Interceptor` para que un eventual 401 (deploy
   * pendiente, gateway, etc.) NO dispare logout global. El caller maneja
   * el error localmente.
   */
  getToggles(): Observable<BancoPlazaTogglesStatus> {
    const url = `${this.baseUrl}banco-plaza/toggles`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<BancoPlazaTogglesStatus>(url, {
          headers: headers.set('X-Skip-Error-Interceptor', '1'),
        }),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Prende / apaga `PAGO_BANCO_PLAZA`.
   *
   * Toggles independientes desde el rediseño F2 (2026-05): pueden coexistir
   * en true con `PAGO_N58`. El handler de 409 queda como dead code defensivo
   * por si en el futuro el backend reintroduce algún conflict-state.
   */
  setPagoBancoPlaza(enabled: boolean): Observable<BancoPlazaTogglesStatus> {
    const url = `${this.baseUrl}banco-plaza/toggles/pago`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<BancoPlazaTogglesStatus>(url, { enabled }, { headers }),
      ),
      catchError((err: HttpErrorResponse) => this.handleToggleError(err)),
    );
  }

  getBPMontoMin(): Observable<{ valor: number }> {
    const url = `${this.baseUrl}banco-plaza/toggles/monto-min`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ valor: number }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  setBPMontoMin(valor: number): Observable<BancoPlazaTogglesStatus> {
    const url = `${this.baseUrl}banco-plaza/toggles/monto-min`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<BancoPlazaTogglesStatus>(url, { valor }, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  getBPCuentaTrabajo(): Observable<{ valor: number | null }> {
    const url = `${this.baseUrl}banco-plaza/toggles/cuenta-trabajo`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<{ valor: number | null }>(url, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  setBPCuentaTrabajo(valor: number): Observable<BancoPlazaTogglesStatus> {
    const url = `${this.baseUrl}banco-plaza/toggles/cuenta-trabajo`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<BancoPlazaTogglesStatus>(url, { valor }, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Error handler que preserva `status` y `conflictWith` para el caller.
   * `handleError` genérico colapsa todo a un string, lo que esconde el 409.
   */
  private handleToggleError(error: HttpErrorResponse): Observable<never> {
    const payload = error.error ?? {};
    const msg =
      typeof payload === 'object' && payload.message
        ? payload.message
        : `Error del servidor: ${error.status}`;
    const out: BancoPlazaToggleError = {
      status: error.status as any,
      message: msg,
      conflictWith: payload.conflictWith,
    };
    return throwError(() => out);
  }

  // ─── Consulta de liquidación BCV (botón "Consultar BCV") ─────────

  consultarLiquidacion(query: {
    id: string;
    cuenta: string;
    referencia: string;
    monto: string;
    fecha: string;
  }): Observable<{ success: boolean; data: any; header: any }> {
    const url = `${this.baseUrl}banco-plaza/pagos/liquidacion`;
    const params: Record<string, string> = { ...query };
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers, params })),
      catchError(this.handleError),
    );
  }

  /**
   * Cuota diaria USD BP — cuánto se usó hoy contra el cap contractual y
   * cuánto queda disponible. Mismo cálculo que usa el gate del payout
   * internamente. Útil para widget en el dashboard.
   */
  getCuotaDiariaUsd(): Observable<{
    success: boolean;
    data: {
      limiteOperacionUSD: number;
      limiteDiarioUSD: number;
      tasaBcvUsada: number | null;
      acumuladoUSD: number;
      disponibleUSD: number;
      porcentajeUsado: number;
      operacionesHoy: number;
      totalVesHoy: number;
      ventanaDesde: string;
      ventanaHasta: string;
    };
  }> {
    const url = `${this.baseUrl}banco-plaza/cuota-diaria-usd`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers })),
      catchError(this.handleError),
    );
  }

  /**
   * Verifica el estado real BCV de un payout interbank usando el id del log.
   * El backend lee los datos del log y consulta directamente al banco —
   * read-only, no modifica nada. Útil para reconciliar desde el listado
   * sin reingresar manualmente cuenta/referencia/monto/fecha.
   */
  verificarBcv(
    txId: string,
  ): Observable<{
    success: boolean;
    data: {
      txId: string;
      tipoLog: string;
      statusLog: string;
      referenciaBanco: string;
      monto: number;
      codigoRespuesta: string;
      descripcionCliente: string | null;
      descripcionSistema: string | null;
      esAprobada: boolean;
      esPendiente: boolean;
      esRechazada: boolean;
      consultadoEn: string;
    };
  }> {
    const url = `${this.baseUrl}banco-plaza/transactions/${txId}/verificar-bcv`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers })),
      catchError(this.handleError),
    );
  }
}
